import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { pollSignals, sendSignal, type CallSignal, type TeamPerson } from './api'
import type { Pos } from './arena'
import { isInitiator, shouldConnect } from './proximity'

const ICE = [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }]
const TICK_MS = 1500
const CONNECT_TIMEOUT_MS = 20_000

export interface RemoteMedia {
  userId: string
  stream: MediaStream
  hasVideo: boolean
}

interface Conn {
  pc: RTCPeerConnection
  gen: string
  startedAt: number
  stream: MediaStream | null
  connected: boolean
}

const newGen = () => Math.random().toString(36).slice(2, 10)

function gathered(pc: RTCPeerConnection): Promise<void> {
  return new Promise((resolve) => {
    if (pc.iceGatheringState === 'complete') return resolve()
    const done = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', done)
        resolve()
      }
    }
    pc.addEventListener('icegatheringstatechange', done)
    window.setTimeout(resolve, 2_500)
  })
}

/**
 * Voice and video between people standing near each other, browser to browser. The server only
 * carries the handshake. It runs only while the person has their microphone or camera on, so
 * nobody is connected to — or listening for — anyone until they have chosen to be.
 *
 * Each pair has one initiator (the lower id), so two people never offer at once. The offer waits for
 * ICE gathering to finish and carries every candidate, so a call costs two messages, not dozens.
 * Public STUN only: a pair behind strict firewalls may fail to connect, and that is stated in the UI.
 */
export function useProximityCalls({
  enabled,
  meId,
  people,
  posRef,
  localStream,
}: {
  enabled: boolean
  meId: string
  people: readonly TeamPerson[]
  posRef: MutableRefObject<Pos | null>
  localStream: MediaStream | null
}): RemoteMedia[] {
  const [remotes, setRemotes] = useState<RemoteMedia[]>([])
  const peopleRef = useRef(people)
  useEffect(() => {
    peopleRef.current = people
  }, [people])

  useEffect(() => {
    if (!enabled || !localStream || typeof RTCPeerConnection === 'undefined') return
    const stream = localStream
    const conns = new Map<string, Conn>()
    let stopped = false

    const publish = () =>
      setRemotes(
        [...conns.entries()]
          .filter(([, c]) => c.stream && c.stream.getTracks().length > 0)
          .map(([userId, c]) => ({ userId, stream: c.stream!, hasVideo: c.stream!.getVideoTracks().length > 0 })),
      )

    const close = (peerId: string, bye: boolean) => {
      const c = conns.get(peerId)
      if (!c) return
      conns.delete(peerId)
      try { c.pc.close() } catch { /* already closed */ }
      if (bye) void sendSignal({ to: peerId, kind: 'bye', gen: c.gen }).catch(() => undefined)
      if (!stopped) publish()
    }

    const attachLocal = (pc: RTCPeerConnection) => {
      const audio = stream.getAudioTracks()[0] ?? null
      const video = stream.getVideoTracks()[0] ?? null
      for (const t of pc.getTransceivers()) {
        const track = t.receiver.track.kind === 'audio' ? audio : video
        t.direction = 'sendrecv'
        void t.sender.replaceTrack(track)
      }
    }

    const makePc = (peerId: string, gen: string): Conn => {
      const pc = new RTCPeerConnection({ iceServers: ICE })
      const conn: Conn = { pc, gen, startedAt: Date.now(), stream: null, connected: false }
      pc.ontrack = (e) => {
        // Tracks added with replaceTrack arrive without a stream, so gather them into one.
        const st = e.streams[0] ?? conn.stream ?? new MediaStream()
        if (!st.getTracks().includes(e.track)) st.addTrack(e.track)
        conn.stream = st
        publish()
      }
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') conn.connected = true
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') close(peerId, false)
      }
      conns.set(peerId, conn)
      return conn
    }

    const startCall = async (peerId: string) => {
      const gen = newGen()
      const conn = makePc(peerId, gen)
      try {
        conn.pc.addTransceiver('audio', { direction: 'sendrecv' })
        conn.pc.addTransceiver('video', { direction: 'sendrecv' })
        attachLocal(conn.pc)
        await conn.pc.setLocalDescription(await conn.pc.createOffer())
        await gathered(conn.pc)
        if (stopped || conns.get(peerId) !== conn) return
        await sendSignal({ to: peerId, kind: 'offer', gen, data: JSON.stringify(conn.pc.localDescription) })
      } catch {
        close(peerId, false)
      }
    }

    const handle = async (sig: CallSignal) => {
      const have = conns.get(sig.from)
      if (sig.kind === 'bye') {
        if (have && have.gen === sig.gen) close(sig.from, false)
        return
      }
      if (sig.kind === 'answer') {
        if (have && have.gen === sig.gen && have.pc.signalingState === 'have-local-offer') {
          try { await have.pc.setRemoteDescription(JSON.parse(sig.data)) } catch { close(sig.from, false) }
        }
        return
      }
      // An offer: only from someone I could be on a call with, and only if I would not be the one offering.
      if (isInitiator(meId, sig.from)) return
      if (have) close(sig.from, false)
      const conn = makePc(sig.from, sig.gen)
      try {
        await conn.pc.setRemoteDescription(JSON.parse(sig.data))
        attachLocal(conn.pc)
        await conn.pc.setLocalDescription(await conn.pc.createAnswer())
        await gathered(conn.pc)
        if (stopped || conns.get(sig.from) !== conn) return
        await sendSignal({ to: sig.from, kind: 'answer', gen: sig.gen, data: JSON.stringify(conn.pc.localDescription) })
      } catch {
        close(sig.from, false)
      }
    }

    const tick = async () => {
      try {
        for (const sig of await pollSignals()) await handle(sig)
      } catch { /* try again next tick */ }
      if (stopped) return
      const me = posRef.current
      if (!me) return
      const byId = new Map(peopleRef.current.map((p) => [p.userId, p]))
      for (const [peerId, conn] of [...conns]) {
        const want = shouldConnect(me, true, byId.get(peerId)?.pos ?? null, true)
        const stuck = !conn.connected && Date.now() - conn.startedAt > CONNECT_TIMEOUT_MS
        if (!want || stuck) close(peerId, true)
      }
      for (const p of peopleRef.current) {
        if (p.userId === meId || conns.has(p.userId)) continue
        if (shouldConnect(me, true, p.pos, false) && isInitiator(meId, p.userId)) void startCall(p.userId)
      }
    }

    void tick()
    const id = window.setInterval(() => void tick(), TICK_MS)
    return () => {
      stopped = true
      window.clearInterval(id)
      for (const peerId of [...conns.keys()]) close(peerId, true)
      setRemotes([])
    }
  }, [enabled, localStream, meId, posRef])

  return remotes
}
